/** @jsxRuntime automatic */
import {
  Children,
  cloneElement,
  forwardRef,
  isValidElement,
  useCallback,
  useRef,
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  type ReactElement,
  type ReactNode,
} from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { useArming, useBusyFade, type ArmKey } from "./swap-boot";
import { cn } from "./utils";
import { Spinner } from "./spinner";

/*
 * Button (spec 07 › Design system › Botones).
 *
 * - The primary CTA is ink (`--primary`), never the brand violet. Hover changes
 *   the color; disabled is an explicit grey fill, never opacity.
 * - `[&:disabled:not([aria-busy=true])]` keeps a loading button in its own
 *   colors: loading reads as "working", not as "unavailable".
 * - Visual height and touch target are separate: every size keeps a 44 px
 *   target with a finger and only shrinks with a mouse in the wide layout
 *   (`md:pointer-fine:`).
 * - Press (`press`, spec 07 › Foco e interacción): the color at 0 ms and the
 *   scale to .98 in 60 (motion-safe), back in 160; a hover tints at once and
 *   lets go in 160. Focus: the shared brand outline, never interpolated.
 * - Width (visual audit 2026-10-13, P0 «ninguna pantalla más ancha que el
 *   teléfono»): every button is `max-w-full`, so a label built from data (a
 *   company, a resource, a job title) can never push the page wider than the
 *   phone. Long labels either wrap (`textWrap="wrap"`, the default for full
 *   width) or truncate with an ellipsis (`textWrap="truncate"`, full label in
 *   `title`). A link that looks like a button is `ButtonLink`, never
 *   `buttonVariants()` on a bare `<a>`: only the components render the inner
 *   label span that truncates.
 */
const buttonVariantsBase = cva(
  "relative inline-flex max-w-full shrink-0 cursor-pointer select-none items-center justify-center gap-2 rounded-md font-medium press motion-safe:active:scale-[.98] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:pointer-events-none disabled:cursor-not-allowed disabled:shadow-none aria-busy:cursor-progress [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        /** Ink CTA — one solid primary per viewport. */
        default:
          "bg-primary text-primary-foreground shadow-xs hover:bg-primary-hover [&:disabled:not([aria-busy=true])]:bg-disabled [&:disabled:not([aria-busy=true])]:text-disabled-foreground",
        /** Neutral filled secondary. */
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary-hover [&:disabled:not([aria-busy=true])]:bg-disabled [&:disabled:not([aria-busy=true])]:text-disabled-foreground",
        /** Brand-subtle fill: the next step inside a card. Replaces `bg-primary/10`. */
        tonal:
          "bg-brand-subtle text-brand-subtle-foreground hover:bg-brand-subtle-hover [&:disabled:not([aria-busy=true])]:bg-disabled [&:disabled:not([aria-busy=true])]:text-disabled-foreground",
        outline:
          "border border-outline bg-transparent text-foreground shadow-xs hover:border-input hover:bg-hover [&:disabled:not([aria-busy=true])]:border-border [&:disabled:not([aria-busy=true])]:bg-transparent [&:disabled:not([aria-busy=true])]:text-disabled-foreground",
        ghost:
          "text-foreground hover:bg-hover active:bg-pressed [&:disabled:not([aria-busy=true])]:text-disabled-foreground",
        destructive:
          "bg-destructive text-destructive-foreground shadow-xs hover:bg-destructive-hover [&:disabled:not([aria-busy=true])]:bg-disabled [&:disabled:not([aria-busy=true])]:text-disabled-foreground",
        /**
         * The primary on an ink band (marketing closing CTA): background-colored
         * in light; in dark the band is a raised card, so it is the ink primary.
         */
        inverse:
          "bg-background text-foreground shadow-xs hover:bg-hover dark:bg-primary dark:text-primary-foreground dark:hover:bg-primary-hover [&:disabled:not([aria-busy=true])]:bg-disabled [&:disabled:not([aria-busy=true])]:text-disabled-foreground",
        /** Text action in running copy: brand, underlined at rest (no hover on touch). */
        link: "h-auto min-h-0 px-0 py-0 text-link underline decoration-current/40 underline-offset-4 hover:decoration-current motion-safe:active:scale-100 [&:disabled:not([aria-busy=true])]:text-disabled-foreground",
      },
      size: {
        /*
         * Height and type both separate touch from mouse (components-05): with a
         * finger a label is never under 14 px (a 12 px label on a 44 px target
         * read smaller than the 15 px body it sits in); the dense 12/14 px
         * labels are for a mouse in the wide layout only.
         */
        /** 28 px and 12 px with a mouse; 44 px and 14 px with a finger. */
        xs: "min-h-11 gap-1.5 px-2.5 text-sm md:pointer-fine:h-7 md:pointer-fine:min-h-7 md:pointer-fine:text-xs [&_svg]:size-3.5",
        /** 32 px and 12 px with a mouse in the wide layout; 44 px and 14 px everywhere else. */
        sm: "min-h-11 gap-1.5 px-3 text-sm md:pointer-fine:h-8 md:pointer-fine:min-h-8 md:pointer-fine:text-xs [&_svg]:size-4",
        /** 36 px and 14 px with a mouse in the wide layout; 44 px and the 15 px body everywhere else. */
        md: "min-h-11 px-4 text-body md:pointer-fine:h-9 md:pointer-fine:min-h-9 md:pointer-fine:text-sm [&_svg]:size-4",
        /** Legacy default: 44 px everywhere. */
        default: "min-h-11 px-4 py-2 text-sm [&_svg]:size-4",
        lg: "min-h-11 px-6 text-base [&_svg]:size-5",
        /**
         * A text link that is easy to hit (mobile-craft-18, secondary-v01): it
         * keeps the font size it is given (`text-label`…). With
         * `variant="link"` the box stays flush with the text and the 44 px
         * target is the link's invisible band (compound variant below,
         * inbox-31): «Ver la oferta», «Abrir Perfil →», «Quitar foto».
         */
        text: "min-h-11 gap-1 md:pointer-fine:min-h-0 [&_svg]:size-3.5",
        /** 48 px: the sticky primary on phones and auth. */
        xl: "min-h-12 px-6 text-base [&_svg]:size-5",
        icon: "h-11 min-h-11 w-11 min-w-11 [&_svg]:size-5",
        /**
         * A dense icon inside a row (the inbox row's ⋯, Guardar, Descartar):
         * 28 px drawn, 44 px to the finger through an invisible band.
         */
        "icon-xs":
          "relative size-7 min-h-7 min-w-7 after:absolute after:-inset-2 after:content-[''] [&_svg]:size-4",
        /** Icon button that shrinks to 32 px with a mouse in the wide layout. */
        "icon-sm":
          "h-11 min-h-11 w-11 min-w-11 md:pointer-fine:h-8 md:pointer-fine:min-h-8 md:pointer-fine:w-8 md:pointer-fine:min-w-8 [&_svg]:size-4",
      },
      textWrap: {
        /** Default: one line. Long labels should be shortened, not wrapped. */
        nowrap: "whitespace-nowrap",
        /** Full-width CTAs and labels with data: wraps, and may shrink in a row. */
        wrap: "min-w-0 shrink whitespace-normal text-balance py-2 text-center leading-tight",
        /**
         * One line, ellipsis when the container is too narrow (label in `title`).
         * With large text on a narrow screen (under 20em of the person's own
         * font size: a phone with the text at 150 % or more) the label takes
         * two lines before it is cut (a11y-09).
         */
        truncate: "min-w-0 max-w-full shrink whitespace-nowrap max-[20em]:whitespace-normal max-[20em]:py-1.5",
      },
      fullWidth: {
        true: "w-full",
        false: "",
      },
    },
    compoundVariants: [
      {
        /*
         * A link sits in its line of text, flush with it (inbox-31): no size
         * adds its padding or height (they came after the variant and won in
         * tailwind-merge, so "Ver planes" started 16 px in). The 44 px target
         * is an invisible band above and below the text.
         */
        variant: "link",
        class:
          "h-auto min-h-0 px-0 py-0 md:pointer-fine:h-auto md:pointer-fine:min-h-0 after:absolute after:inset-x-0 after:-inset-y-3 after:content-['']",
      },
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
      textWrap: "nowrap",
      fullWidth: false,
    },
  },
);

type ButtonVariantProps = VariantProps<typeof buttonVariantsBase>;
type TextWrap = NonNullable<ButtonVariantProps["textWrap"]>;

/**
 * Full-width buttons wrap by default: a long translated CTA must not overflow a
 * phone. An explicit `textWrap` always wins.
 */
function resolveTextWrap(
  textWrap: ButtonVariantProps["textWrap"],
  fullWidth: ButtonVariantProps["fullWidth"],
  className: string | undefined,
): TextWrap {
  if (textWrap) return textWrap;
  if (fullWidth) return "wrap";
  return className && /(^|\s)w-full(\s|$)/.test(className) ? "wrap" : "nowrap";
}

/**
 * Button classes for a custom element. Applies the same wrap rule as `Button`
 * (full width → wrap). Prefer `Button` or `ButtonLink`: a bare element with
 * these classes has no inner label span, so `truncate` cannot add the ellipsis.
 */
function buttonVariants(props: Parameters<typeof buttonVariantsBase>[0] = {}): string {
  const className = typeof props?.className === "string" ? props.className : undefined;
  // Merged, so a later class (a compound variant, the caller's) wins as in `Button`.
  return cn(buttonVariantsBase({ ...props, textWrap: resolveTextWrap(props?.textWrap, props?.fullWidth, className) }));
}

interface ButtonContentProps {
  /** Icon before the label (sized by the button). */
  leadingIcon?: ReactNode;
  /** Icon after the label (sized by the button). */
  trailingIcon?: ReactNode;
}

/** The label with its icons; truncation wraps the label in its own shrinking span. */
function buttonContent(children: ReactNode, wrap: TextWrap, { leadingIcon, trailingIcon }: ButtonContentProps) {
  const label =
    wrap === "truncate" ? (
      <span className="min-w-0 truncate max-[20em]:line-clamp-2 max-[20em]:whitespace-normal max-[20em]:leading-tight">{children}</span>
    ) : (
      children
    );
  if (!leadingIcon && !trailingIcon) return label;
  return (
    <>
      {leadingIcon}
      {label}
      {trailingIcon}
    </>
  );
}

/** Truncated text keeps its full label in `title` (when the label is plain text). */
function truncateTitle(wrap: TextWrap, children: ReactNode, title: string | undefined) {
  if (title !== undefined || wrap !== "truncate") return title;
  return typeof children === "string" || typeof children === "number" ? String(children) : undefined;
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, ButtonVariantProps, ButtonContentProps {
  /**
   * Shows a centered spinner over the (hidden) label, so the width never
   * changes, marks the button `aria-busy` and blocks clicks. Keeps the
   * variant's colors: loading is "working", not "disabled". It stays
   * focusable (`aria-disabled`, not `disabled`): a disabled button drops the
   * keyboard focus to `body`, so an error after «Iniciar sesión» left the
   * person at the top of the page (a11y-04). A change after the mount
   * crosses the label and the spinner in place (120 ms).
   */
  loading?: boolean;
  /**
   * The arming rule (spec 07 › Movimiento): what the button does now. When
   * it changes, the button has turned in place into a confirmation or
   * another action with consequences («Enviar» → «Sí, enviar», «Eliminar» →
   * «¿Eliminar?», marking as sent, paying), and the same double tap must
   * never confirm it: see `useArming`.
   */
  armKey?: ArmKey;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      textWrap,
      fullWidth,
      type = "button",
      loading = false,
      armKey,
      disabled,
      leadingIcon,
      trailingIcon,
      title,
      children,
      onClick,
      ...props
    },
    ref,
  ) => {
    const wrap = resolveTextWrap(textWrap, fullWidth, className);
    const content = buttonContent(children, wrap, { leadingIcon, trailingIcon });
    // Busy keeps the focus: `aria-disabled` and a swallowed click (which also
    // cancels a form's submit and Enter's implicit submission) instead of `disabled`.
    const busy = loading && !disabled;
    const { shown, wrapped, label, spinner, onSpinnerEnd } = useBusyFade(loading);
    const own = useRef<HTMLButtonElement | null>(null);
    const setRef = useCallback(
      (node: HTMLButtonElement | null) => {
        own.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) ref.current = node;
      },
      [ref],
    );
    useArming(own, armKey);
    return (
      <button
        type={type}
        className={cn(buttonVariantsBase({ variant, size, textWrap: wrap, fullWidth, className }))}
        // Only an arming button needs its node: every other one keeps the caller's ref as it is.
        ref={armKey === undefined ? ref : setRef}
        disabled={disabled}
        aria-disabled={busy || undefined}
        aria-busy={loading || undefined}
        data-loading={loading || undefined}
        title={truncateTitle(wrap, children, title)}
        onClick={
          busy
            ? (event) => {
                event.preventDefault();
                event.stopPropagation();
              }
            : onClick
        }
        {...props}
      >
        {wrapped ? (
          // Transparent, not `invisible`: the label keeps the width and stays the
          // accessible name while busy (a hidden label left the button nameless).
          // Once wrapped it stays, `contents` at rest (the bare label's layout):
          // the label never remounts when a wait ends.
          <span
            ref={label}
            data-label=""
            data-busy={shown ? (loading ? "off" : "on") : undefined}
            className={shown ? "inline-flex min-w-0 items-center gap-[inherit]" : "contents"}
          >
            {content}
          </span>
        ) : (
          content
        )}
        {shown && (
          <span
            ref={spinner}
            data-busy={loading ? "on" : "off"}
            className="absolute inset-0 grid place-items-center"
            aria-hidden="true"
            onTransitionEnd={onSpinnerEnd}
          >
            <Spinner size="sm" />
          </span>
        )}
      </button>
    );
  },
);
Button.displayName = "Button";

export interface ButtonLinkProps
  extends AnchorHTMLAttributes<HTMLAnchorElement>,
    ButtonVariantProps,
    ButtonContentProps {
  /**
   * Render the single child element (a router `<Link>`) with the button skin
   * instead of an `<a>`: its own children become the label.
   */
  asChild?: boolean;
}

/**
 * A link that looks like a button (spec 07 › Botones): same variants, sizes,
 * width rules and label span as `Button`, so a label with data wraps or
 * truncates instead of pushing the page. `asChild` styles a router `<Link>`:
 *
 *   <ButtonLink href={url} target="_blank" textWrap="wrap">…</ButtonLink>
 *   <ButtonLink asChild variant="outline" size="sm"><Link to="/app">…</Link></ButtonLink>
 */
export const ButtonLink = forwardRef<HTMLAnchorElement, ButtonLinkProps>(
  (
    { className, variant, size, textWrap, fullWidth, asChild = false, leadingIcon, trailingIcon, title, children, ...props },
    ref,
  ) => {
    const wrap = resolveTextWrap(textWrap, fullWidth, className);
    const classes = cn(buttonVariantsBase({ variant, size, textWrap: wrap, fullWidth, className }));
    if (asChild) {
      const child = Children.only(children);
      if (!isValidElement<{ className?: string; children?: ReactNode; title?: string }>(child)) return null;
      const label = child.props.children;
      return cloneElement(child as ReactElement<Record<string, unknown>>, {
        ...props,
        ref,
        className: cn(classes, child.props.className),
        title: truncateTitle(wrap, label, title ?? child.props.title),
        children: buttonContent(label, wrap, { leadingIcon, trailingIcon }),
      });
    }
    return (
      <a ref={ref} className={classes} title={truncateTitle(wrap, children, title)} {...props}>
        {buttonContent(children, wrap, { leadingIcon, trailingIcon })}
      </a>
    );
  },
);
ButtonLink.displayName = "ButtonLink";

export { buttonVariants };
